<script setup>
  import {computed, onMounted, ref, watch} from "vue";

  import ComponentAutocomplete from "@components/Autocomplete/Autocomplete.vue";
  import ComponentSearchWrapper from "@components/Autocomplete/SearchWrapper.vue";
  import {useForm, usePage} from "@inertiajs/vue3";
  import {recommendedLaborKeys} from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";

  defineOptions({
    name: "LaborSearch",
  });

  const props = defineProps({
    laborValues: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      required: true,
    },
  });

  const page = usePage();

  const refSearch = ref(null);
  const error = ref("");

  const recommendedValueTypes = ref([]);

  const laborvalueForm = useForm({
    type: null,
    deviation: "normal",
    value: null,
    mc_laborvalue_id: null,
    mc_laborvalue_key: null,
    mc_unit_id: null,
    unit: null,
  });

  const isDisabled = computed(() => laborvalueForm.processing || props.disabled);

  onMounted(() => setRecommendedLaborvalues());

  watch(
    () => props.laborValues,
    () => {
      setRecommendedLaborvalues();
    },
    {deep: true},
  );

  const setRecommendedLaborvalues = () => {
    coreRouter.searchForLaborvaluesByKey(recommendedLaborKeys.join(","), {
      onSuccess: (res) => {
        if (res.data?.length > 0) {
          let data = res.data;

          data = data.filter((item) => item.default);

          data = data.filter(
            (item) => !props.laborValues.some((laborvalue) => laborvalue.mc_laborvalue_key === item.key),
          );

          recommendedValueTypes.value = data.slice(0, 4);
        }
      },
      onError: () => {
        recommendedValueTypes.value = [];
      },
    });
  };

  const fetchLaborvalues = (searchTerm) => {
    return new Promise((resolve) => {
      coreRouter.get("/api/v1/am/laborvalues/?search=name:" + searchTerm, {
        onSuccess: (response) => {
          filterOptionList(response.data).then((filteredList) => {
            resolve(filteredList);
          });
        },
        onError: () => {
          resolve(false);
        },
      });
    });
  };

  const filterOptionList = (list) => {
    return new Promise((resolve) => {
      const defaultValues = new Set([182, 183, 3, 1, 177, 137, 4]);

      const filteredList = list
        .filter((item) => {
          const isDefaultValue = defaultValues.has(item.key);
          return !isDefaultValue;
        })
        .map((item) => {
          const existsInRecord = page.props.laborvalues?.data.find(
            (recordItem) => recordItem.mc_laborvalue_key === item.key,
          );

          return {...item, exists: existsInRecord?.type || false};
        });

      resolve(filteredList);
    });
  };

  const updateRowWithSelectedItem = (selectItem) => {
    if (!selectItem.id) {
      error.value = "Unbekannte Auswahl!";
      return;
    }

    error.value = "";
    refSearch.value.reset();

    laborvalueForm.type = selectItem.name;
    laborvalueForm.mc_laborvalue_id = selectItem.id;
    laborvalueForm.mc_laborvalue_key = selectItem.key;

    laborvalueForm.post(route("laborvalues.store", {patient: page.props.patient.id, record: page.props.record.id}), {
      preserveScroll: true,
      only: ["symptoms", "laborvalues", "diseases", "flash", "errors"],
      onFinish: () => {
        laborvalueForm.reset();
      },
    });
  };

  const setFocus = () => {
    refSearch.value.focus();
  };
</script>

<template>
  <ComponentSearchWrapper :set-focus="setFocus" :disabled="isDisabled" class="my-4" :test-id="getTestId('search')">
    <ComponentAutocomplete
      ref="refSearch"
      class="ml-1 w-96"
      label="Labor- oder Vitalwert"
      placeholder="Suchen"
      :fetch-method="fetchLaborvalues"
      key-name="name"
      :min-search-term-length="1"
      :validation="error"
      :disabled="isDisabled"
      :test-id="getTestId('search')"
      @selected="updateRowWithSelectedItem"
    >
      <template #autocomplete-item="{item}">
        <div class="min-h-10 w-full">
          <span class="inline-block align-middle">
            {{ item.name }}
          </span>

          <div v-if="item.exists" class="text-xs text-mcred-500">
            Laborwert wurde bereits erfasst als {{ item.exists }}
          </div>
        </div>
      </template>

      <template #autocomplete-recommendation>
        <div v-if="recommendedValueTypes.length > 0" class="p-3">
          <span class="text-gray-500">Häufig verwendete Werte:</span>
          <ul class="flex flex-wrap">
            <li
              v-for="(recommendation, index) in recommendedValueTypes"
              :key="index"
              class="last:after:content-[] mr-2 after:content-[',']"
            >
              <button
                class="cursor-pointer text-mcred hover:underline"
                @click.prevent="updateRowWithSelectedItem(recommendation)"
              >
                {{ recommendation.name }}
              </button>
            </li>
          </ul>
        </div>
      </template>

      <template #autocomplete-empty>
        <div class="flex h-16 w-96 items-stretch">
          <div class="w-full self-center text-center">
            <span>Zu Ihrer Eingabe konnte nichts gefunden werden.</span>
          </div>
        </div>
      </template>
    </ComponentAutocomplete>
  </ComponentSearchWrapper>
</template>
